import { Controller } from '@hotwired/stimulus'

// A tag/chip field for a LIST of email addresses: the member types one and
// presses Enter or comma to commit it as a removable chip; each chip is backed
// by a hidden <input name="<model>[<param>][]"> so the list posts as a string
// array the server normalizes + de-dups. Used for the additional-To, CC, and BCC
// fields on the compose + reply forms, and for a mailbox's send-as aliases
// (#342) — hence "email" rather than "recipient", which named a draft concept.
//
// modelValue carries NO default. The default lives in ONE place — the Ruby local
// in form/_email_chips_field.html.erb, which emits the data attribute
// unconditionally — so a JS default could only ever fire when the attribute is
// missing, i.e. when the wiring is already broken, and it would paper over that
// by silently naming a mailbox's chips `draft[...]`. The local is also why the
// value alone is not enough: chips rendered server-side are named in ERB before
// this controller ever connects, and a value cannot reach markup that exists.
//
// Progressive enhancement: any server-rendered chip carries its own hidden
// input already, so this controller only wires the add/remove interactions — it
// never owns the canonical list, the hidden inputs do. Server-side validation
// remains the source of truth; a bad address typed here still fails the form
// rather than silently dropping.
export default class extends Controller {
  static targets = ['input', 'chips']
  static values = { param: String, model: String }

  connect() {
    // Nothing to initialize — markup + any server-rendered chips are already in
    // place; we only respond to input/click actions below.
  }

  disconnect() {
    // No listeners, timers, or instances held (Turbo-lifecycle convention).
  }

  // Enter or comma commits the current input as a chip; Backspace on an empty
  // input removes the last chip (a familiar tag-field affordance).
  keydown(event) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      this.commit()
    } else if (event.key === 'Backspace' && this.inputTarget.value === '') {
      this.removeLast()
    }
  }

  // Commit on blur too, so a typed-but-not-Entered address isn't silently lost
  // when the member tabs/clicks away to submit.
  commit() {
    const value = this.inputTarget.value.trim()
    if (value === '') return

    this.addChip(value)
    this.inputTarget.value = ''
  }

  addChip(email) {
    const chip = document.createElement('span')
    chip.className = 'badge text-bg-light border d-inline-flex align-items-center gap-1 me-1 mb-1'

    const label = document.createElement('span')
    label.textContent = email
    chip.appendChild(label)

    const hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = `${this.modelValue}[${this.paramValue}][]`
    hidden.value = email
    chip.appendChild(hidden)

    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className = 'btn-close'
    remove.setAttribute('aria-label', `Remove ${email}`)
    // Bind the removal directly (rather than a data-action) so a chip added after
    // connect() works without waiting for Stimulus's action re-scan.
    remove.addEventListener('click', () => chip.remove())
    chip.appendChild(remove)

    this.chipsTarget.appendChild(chip)
  }

  // Server-rendered chips (an edit form's stored recipients) carry a data-action
  // instead — this handles their removal click.
  remove(event) {
    event.target.closest('.badge').remove()
  }

  removeLast() {
    const chips = this.chipsTarget.querySelectorAll('.badge')
    if (chips.length > 0) chips[chips.length - 1].remove()
  }
}
